import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import makeStyles from '@mui/styles/makeStyles';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import { Loader } from 'ui';
import { theme } from 'theme';
import { DoughnutChart } from 'ui/Charts';
import { ErrorBoundary } from 'shared/errors';
import { Statistics, StatisticsItem, ChartData } from 'modules/common/PortfolioManager/types';
import { useReportPage } from 'shared/hooks';
import PortfolioChartLegend from '../../common/PortfolioManager/Portfolio/components/PortfolioChartLegend';

const useStyles = makeStyles({
  divider: {
    alignSelf: 'stretch',
    height: 'auto',
    margin: theme.spacing(0, 2),
  },
  companies: {
    marginTop: theme.spacing(6),
  },
  bold: {
    fontWeight: 900,
  },
  title: {
    paddingLeft: theme.spacing(2),
    fontSize: useReportPage ? 12 : 16,
  },
});

type ESGLendingDataProps = {
  statistics?: Statistics;
  noOfItems?: number;
  loading?: boolean;
};

const chartOtherThreshold = 5;

const ESGLendingData: FC<ESGLendingDataProps> = ({ statistics, loading = false }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const { chartShort, chartExtended } = theme.customPalettes;

  const sortChartData = (objectToSort: ChartData) =>
    Object.fromEntries(Object.entries(objectToSort).sort(([, a], [, b]) => b - a));
  const chartItemsToArray = (chartData: ChartData): StatisticsItem[] =>
    Object.keys(chartData).map(name => ({ name, count: chartData[name] }));

  const calculateValuesToDecimal = (statisticsItems) => {
    const roundedItems = statisticsItems.map(item => ({
      name: item.name,
      count: item.exposure * 100,
    })
    );

    const sortedItems = roundedItems.sort(
      (a, b) => Math.floor(a.count) - a.count - (Math.floor(b.count) - b.count),
    );
    return sortedItems.map(item => ({
      name: item.name,
      count: +item.count.toFixed(1),
    })
    );
  };

  const prepareChartData = (statisticsItems?: StatisticsItem[]) => {
    const chartData: ChartData = {};

    if (!statisticsItems) {
      return chartData;
    }

    const roundedStatistics = calculateValuesToDecimal(statisticsItems);

    roundedStatistics.forEach(item => {
      if (item.count < chartOtherThreshold) {
        if (!chartData[t('OTHER')]) {
          chartData[t('OTHER')] = 0;
        }
        chartData[t('OTHER')] += item.count;
      } else {
        chartData[item.name || t('UNKNOWN')] = item.count;
      }
    });

    if (chartData[t('OTHER')]) {
      chartData[t('OTHER')] = +(+chartData[t('OTHER')].toFixed(1))
    }

    return sortChartData(chartData);
  };

  const prepareOtherDataToExpand = (statisticsItems?: StatisticsItem[]) => {
    const otherDataToExpand: ChartData = {};

    if (!statisticsItems) {
      return otherDataToExpand;
    }

    const roundedStatistics = calculateValuesToDecimal(statisticsItems);

    roundedStatistics.forEach(item => {
      if (item.count < chartOtherThreshold) {
        otherDataToExpand[item.name || t('UNKNOWN')] = item.count;
      }
    });
    return sortChartData(otherDataToExpand);
  };

  const regionsChartData: ChartData = prepareChartData(statistics?.regions);
  const industriesChartData: ChartData = prepareChartData(statistics?.industries);
  const otherRegionsToExpand: ChartData = prepareOtherDataToExpand(statistics?.regions);
  const otherIndustriesToExpand: ChartData = prepareOtherDataToExpand(statistics?.industries);

  const sortedRegions = chartItemsToArray(regionsChartData);
  const otherRegionsToExpandArray = chartItemsToArray(otherRegionsToExpand);
  const sortedIndustries = chartItemsToArray(industriesChartData);
  const otherIndustriesToExpandArray = chartItemsToArray(otherIndustriesToExpand);

  return (
    <>
      <Box p={useReportPage ? 0 : 2}>
        <Typography variant="h6">{t('ESG.LENDING_PORTFOLIO_DATA')}</Typography>
        <Box py={2} pt={useReportPage ? 0 : 2}>
          <Divider />
        </Box>
      </Box>
      <Box p={2} pt={useReportPage ? 1 : 2} pb={5}>
        {loading ? (
          <Loader center />
        ) : (
          <Box display="flex" flexDirection={useReportPage ? 'column' : 'row'}>
            <Box flexGrow={1} padding={2}>
              <Typography variant="subtitle1" className={clsx(classes.title, classes.bold)}>
                {t('ESG.EXPOSURE_BY_REGION')}
              </Typography>

              <Box py={2} pt={useReportPage ? 1 : 2}>
                <Divider />
              </Box>

              <Box display="flex" justifyContent="center" width={1}>
                <ErrorBoundary>
                  <DoughnutChart
                    data={regionsChartData}
                    width={400}
                    height={300}
                    margin={0}
                    colors={sortedRegions.length > 5 ? chartExtended : chartShort}
                  />
                </ErrorBoundary>
              </Box>

              <Box py={2} pt={useReportPage ? 1 : 2}>
                <Divider />
              </Box>
              <PortfolioChartLegend
                items={sortedRegions}
                colors={sortedRegions.length > 5 ? chartExtended : chartShort}
                otherItemsToExpand={otherRegionsToExpandArray}
              />
            </Box>

            <Divider className={classes.divider} orientation="vertical" />

            <Box flexGrow={1} padding={2}>
              <Typography variant="subtitle1" className={clsx(classes.title, classes.bold)}>
                {t('ESG.EXPOSURE_BY_INDUSTRY')}
              </Typography>

              <Box py={2} pt={useReportPage ? 1 : 2}>
                <Divider />
              </Box>

              <Box display="flex" justifyContent="center" width={1}>
                <ErrorBoundary>
                  <DoughnutChart
                    data={industriesChartData}
                    width={400}
                    height={300}
                    margin={0}
                    colors={sortedIndustries.length > 5 ? chartExtended : chartShort}
                  />
                </ErrorBoundary>
              </Box>

              <Box py={2} pt={useReportPage ? 1 : 2}>
                <Divider />
              </Box>
              <PortfolioChartLegend
                items={sortedIndustries}
                colors={sortedIndustries.length > 5 ? chartExtended : chartShort}
                otherItemsToExpand={otherIndustriesToExpandArray}
              />
            </Box>
          </Box>
        )}
      </Box>
    </>
  );
};
export default ESGLendingData;
